import * as React from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import { CloseIcon } from "@salt-ds/icons";

import { Button } from "../controls/Button";
import "./drawer.css";

/**
 * Drawer and DrawerCloseButton, replacing `@salt-ds/core`'s.
 *
 * It is the Dialog's sibling and shares its lifecycle exactly: `open` is the caller's intent,
 * `showComponent` is what is on screen, and the drawer stays mounted through its exit animation
 * with a timeout backstop for when animations are disabled. The differences from Dialog are two:
 * it is anchored to an EDGE rather than centred, and `useDismiss` is disabled wholesale rather
 * than only for outside presses.
 *
 * ── THAT `enabled` vs `outsidePress` DIFFERENCE IS THE VENDOR'S, AND IT IS REAL ───────
 *
 * Dialog uses `useDismiss(context, { outsidePress: !disableDismiss })` — Escape still closes it,
 * only the backdrop click is disabled. Drawer uses `{ enabled: !disableDismiss }`, which turns
 * off Escape as well. Copying Dialog's shape here would quietly make a non-dismissable drawer
 * closable by keyboard, so the asymmetry is preserved rather than tidied.
 */
export type DrawerPosition = "top" | "right" | "bottom" | "left";
export type DrawerVariant = "primary" | "secondary";

export interface DrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  position?: DrawerPosition;
  variant?: DrawerVariant;
  disableDismiss?: boolean;
  disableScrim?: boolean;
  initialFocus?: number | React.MutableRefObject<HTMLElement | null>;
}

export const Drawer = React.forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    children,
    className,
    disableDismiss,
    disableScrim,
    initialFocus,
    onOpenChange,
    open = false,
    position = "left",
    variant = "primary",
    ...rest
  },
  ref,
) {
  const [showComponent, setShowComponent] = React.useState(false);
  const { context, refs } = useFloating({ open: showComponent, onOpenChange });
  const { getFloatingProps } = useInteractions([
    useClick(context),
    // `enabled`, not `outsidePress` — see the note above. This turns off Escape too.
    useDismiss(context, { enabled: !disableDismiss }),
  ]);
  const mergedRef = useMergeRefs([refs.setFloating, ref]);

  React.useEffect(() => {
    if (open && !showComponent) {
      setShowComponent(true);
      return;
    }
    if (!open && showComponent) {
      const animate = setTimeout(() => setShowComponent(false), 300);
      return () => clearTimeout(animate);
    }
  }, [open, showComponent]);

  if (!showComponent) return null;

  const classes = ["saltDrawer", `saltDrawer-${position}`];
  classes.push(open ? "saltDrawer-enterAnimation" : "saltDrawer-exitAnimation");
  classes.push(`saltDrawer-${variant}`);
  if (className) classes.push(className);

  const drawer = (
    <FloatingFocusManager context={context} initialFocus={initialFocus} modal outsideElementsInert>
      <div
        aria-modal="true"
        className={classes.join(" ")}
        onAnimationEnd={() => {
          if (!open && showComponent) setShowComponent(false);
        }}
        role="dialog"
        {...getFloatingProps()}
        {...rest}
        ref={mergedRef}
      >
        {children}
      </div>
    </FloatingFocusManager>
  );

  return (
    <FloatingPortal>
      {disableScrim ? (
        drawer
      ) : (
        <FloatingOverlay className="saltScrim saltScrim-fixed" data-testid="scrim" lockScroll>
          {drawer}
        </FloatingOverlay>
      )}
    </FloatingPortal>
  );
});

export type DrawerCloseButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

export const DrawerCloseButton = React.forwardRef<HTMLButtonElement, DrawerCloseButtonProps>(
  function DrawerCloseButton({ className, ...rest }, ref) {
    return (
      // The container is the vendor's and it positions the button against the drawer's edge;
      // the button alone would sit in the content flow.
      <div className="saltDrawerCloseButton-container">
        <Button
          appearance="transparent"
          aria-label="Close Drawer"
          className={className ? `saltDrawerCloseButton ${className}` : "saltDrawerCloseButton"}
          ref={ref}
          {...rest}
        >
          <CloseIcon aria-hidden />
        </Button>
      </div>
    );
  },
);
